import React, { useState } from 'react';
import { SearchIcon, ActivityIcon, ZapIcon, TrendingUpIcon, AlertTriangleIcon, StoreIcon, FactoryIcon, GraduationCapIcon, MapPinIcon, XIcon, DownloadIcon, BellIcon, FileTextIcon } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatZAR } from '../hooks/useMavisData';
const lpuData = [{
  id: 'LPU-001',
  name: 'Eastgate Shopping Centre',
  type: 'Mall',
  location: 'Bedfordview',
  meterId: '04-8001-1001-10',
  consumption: 185400,
  peakDemand: 4.2,
  status: 'Online',
  lastComm: '1 min ago',
  monthlyBill: 463500,
  history: [180, 175, 190, 185, 192, 188, 185]
}, {
  id: 'LPU-002',
  name: 'Toyota SA Manufacturing',
  type: 'Factory',
  location: 'Prospecton, Durban',
  meterId: '04-8002-2002-20',
  consumption: 342000,
  peakDemand: 8.5,
  status: 'Online',
  lastComm: '3 mins ago',
  monthlyBill: 855000,
  history: [340, 335, 350, 342, 338, 345, 342]
}, {
  id: 'LPU-003',
  name: 'Menlyn Park Shopping Centre',
  type: 'Mall',
  location: 'Pretoria',
  meterId: '04-8003-3003-30',
  consumption: 210500,
  peakDemand: 5.1,
  status: 'Warning',
  lastComm: '45 mins ago',
  monthlyBill: 526250,
  history: [200, 205, 215, 220, 210, 208, 210]
}, {
  id: 'LPU-004',
  name: 'Sappi Ngodwana Mill',
  type: 'Factory',
  location: 'Mpumalanga',
  meterId: '04-8004-4004-40',
  consumption: 520000,
  peakDemand: 12.8,
  status: 'Online',
  lastComm: '2 mins ago',
  monthlyBill: 1300000,
  history: [510, 515, 525, 520, 518, 522, 520]
}, {
  id: 'LPU-005',
  name: 'University of Pretoria',
  type: 'School',
  location: 'Hatfield, Pretoria',
  meterId: '04-8005-5005-50',
  consumption: 98500,
  peakDemand: 2.4,
  status: 'Online',
  lastComm: '5 mins ago',
  monthlyBill: 246250,
  history: [95, 98, 100, 97, 99, 96, 98]
}, {
  id: 'LPU-006',
  name: 'Sandton City Complex',
  type: 'Mall',
  location: 'Sandton',
  meterId: '04-8006-6006-60',
  consumption: 275000,
  peakDemand: 6.8,
  status: 'Online',
  lastComm: '1 min ago',
  monthlyBill: 687500,
  history: [270, 268, 280, 275, 272, 278, 275]
}, {
  id: 'LPU-007',
  name: 'ArcelorMittal Vanderbijlpark',
  type: 'Factory',
  location: 'Vanderbijlpark',
  meterId: '04-8007-7007-70',
  consumption: 890000,
  peakDemand: 22.5,
  status: 'Offline',
  lastComm: '6 hours ago',
  monthlyBill: 2225000,
  history: [880, 885, 890, 0, 0, 0, 0]
}, {
  id: 'LPU-008',
  name: 'Wits University',
  type: 'School',
  location: 'Braamfontein, JHB',
  meterId: '04-8008-8008-80',
  consumption: 112000,
  peakDemand: 2.8,
  status: 'Online',
  lastComm: '8 mins ago',
  monthlyBill: 280000,
  history: [108, 110, 115, 112, 109, 113, 112]
}];
const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export function LargePowerUsers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedLpuId, setSelectedLpuId] = useState<string | null>(null);
  const filteredData = lpuData.filter(lpu => {
    const matchesSearch = lpu.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === 'All' || lpu.type === activeFilter;
    return matchesSearch && matchesFilter;
  });
  const selectedLpu = lpuData.find(lpu => lpu.id === selectedLpuId);
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Mall':
        return <StoreIcon className="w-5 h-5" data-id="element-1127" />;
      case 'Factory':
        return <FactoryIcon className="w-5 h-5" data-id="element-1128" />;
      case 'School':
        return <GraduationCapIcon className="w-5 h-5" data-id="element-1129" />;
      default:
        return <StoreIcon className="w-5 h-5" data-id="element-1130" />;
    }
  };
  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'Mall':
        return 'bg-blue-100 text-blue-800';
      case 'Factory':
        return 'bg-purple-100 text-purple-800';
      case 'School':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Online':
        return 'bg-green-100 text-green-800';
      case 'Warning':
        return 'bg-orange-100 text-orange-800';
      case 'Offline':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="p-8 max-w-7xl mx-auto animate-in fade-in duration-500" data-id="element-1131">
      {/* Header */}
      <div className="mb-8" data-id="element-1132">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1133">
          Large Power Users
        </h1>
        <p className="text-gray-500" data-id="element-1134">
          Monitor high-consumption commercial and institutional meters.
        </p>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-1135">
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1136">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-none" data-id="element-1137">
            <ActivityIcon className="w-5 h-5" data-id="element-1138" />
          </div>
          <div data-id="element-1139">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1140">
              Total LPUs
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1141">87</p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1142">
          <div className="p-3 bg-mavis-teal/10 text-mavis-teal rounded-none" data-id="element-1143">
            <ZapIcon className="w-5 h-5" data-id="element-1144" />
          </div>
          <div data-id="element-1145">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1146">
              Total Consumption
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1147">
              1,245,800{' '}
              <span className="text-sm font-sans text-gray-500" data-id="element-1148">kWh</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1149">
          <div className="p-3 bg-mavis-gold/10 text-mavis-gold rounded-none" data-id="element-1150">
            <TrendingUpIcon className="w-5 h-5" data-id="element-1151" />
          </div>
          <div data-id="element-1152">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1153">
              Peak Demand
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1154">
              42.8 <span className="text-sm font-sans text-gray-500" data-id="element-1155">MVA</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-none border border-gray-100 shadow-sm flex items-center gap-4" data-id="element-1156">
          <div className="p-3 bg-mavis-orange/10 text-mavis-orange rounded-none" data-id="element-1157">
            <AlertTriangleIcon className="w-5 h-5" data-id="element-1158" />
          </div>
          <div data-id="element-1159">
            <p className="text-xs text-gray-500 font-medium uppercase" data-id="element-1160">
              Active Alerts
            </p>
            <p className="text-xl font-bold text-gray-900 font-mono" data-id="element-1161">12</p>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-1162">
        <div className="flex border border-gray-200 rounded-none overflow-hidden shadow-sm" data-id="element-1163">
          {['All', 'Malls', 'Factories', 'Schools'].map(tab => {
          const filterValue = tab === 'Malls' ? 'Mall' : tab === 'Factories' ? 'Factory' : tab === 'Schools' ? 'School' : 'All';
          const isActive = activeFilter === filterValue;
          return <button key={tab} onClick={() => setActiveFilter(filterValue)} className={`px-4 py-2 text-sm font-medium transition-colors ${isActive ? 'bg-mavis-teal text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border-r border-gray-200 last:border-r-0'}`} data-id="element-1164">

                {tab}
              </button>;
        })}
        </div>
        <div className="relative w-full md:w-64" data-id="element-1165">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1166" />
          <input type="text" placeholder="Search LPU name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-none focus:ring-mavis-teal focus:border-mavis-teal text-sm shadow-sm" data-id="element-1167" />

        </div>
      </div>

      {/* Table View */}
      <div className="bg-white rounded-none shadow-sm border border-gray-200 overflow-hidden" data-id="element-1168">
        <div className="overflow-x-auto" data-id="element-1169">
          <table className="w-full text-left border-collapse" data-id="element-1170">
            <thead data-id="element-1171">
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-200" data-id="element-1172">
                <th className="p-4 font-medium w-12 text-center" data-id="element-1173">Type</th>
                <th className="p-4 font-medium" data-id="element-1174">Name & ID</th>
                <th className="p-4 font-medium" data-id="element-1175">Location</th>
                <th className="p-4 font-medium" data-id="element-1176">Meter ID</th>
                <th className="p-4 font-medium text-right" data-id="element-1177">Consumption</th>
                <th className="p-4 font-medium text-right" data-id="element-1178">Peak Demand</th>
                <th className="p-4 font-medium text-right" data-id="element-1179">Monthly Bill</th>
                <th className="p-4 font-medium text-center" data-id="element-1180">Status</th>
                <th className="p-4 font-medium w-24" data-id="element-1181">7-Day Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1182">
              {filteredData.map(lpu => <tr key={lpu.id} onClick={() => setSelectedLpuId(lpu.id)} className="hover:bg-gray-50 transition-colors group cursor-pointer" data-id="element-1183">

                  <td className="p-4 text-center text-gray-400" data-id="element-1184">
                    <div className="flex justify-center" data-id="element-1185">
                      {getTypeIcon(lpu.type)}
                    </div>
                  </td>
                  <td className="p-4" data-id="element-1186">
                    <p className="font-medium text-gray-900" data-id="element-1187">{lpu.name}</p>
                    <p className="text-xs text-gray-500 font-mono" data-id="element-1188">{lpu.id}</p>
                  </td>
                  <td className="p-4 text-sm text-gray-600" data-id="element-1189">{lpu.location}</td>
                  <td className="p-4 font-mono text-sm text-gray-900" data-id="element-1190">
                    {lpu.meterId}
                  </td>
                  <td className="p-4 text-right" data-id="element-1191">
                    <span className="font-mono font-bold text-mavis-teal" data-id="element-1192">
                      {lpu.consumption.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500 ml-1" data-id="element-1193">kWh</span>
                  </td>
                  <td className="p-4 text-right" data-id="element-1194">
                    <span className="font-mono font-medium text-gray-900" data-id="element-1195">
                      {lpu.peakDemand.toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500 ml-1" data-id="element-1196">MVA</span>
                  </td>
                  <td className="p-4 text-right font-mono font-medium text-gray-900" data-id="element-1197">
                    {formatZAR(lpu.monthlyBill)}
                  </td>
                  <td className="p-4 text-center" data-id="element-1198">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-none text-xs font-medium ${getStatusBadgeClass(lpu.status)}`} data-id="element-1199">

                      {lpu.status}
                    </span>
                  </td>
                  <td className="p-4" data-id="element-1200">
                    <div className="h-6 w-16 opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-1201">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-1202">
                        <LineChart data={lpu.history.map((val, i) => ({
                      val,
                      i
                    }))} data-id="element-1203">

                          <YAxis domain={['dataMin - 10', 'dataMax + 10']} hide data-id="element-1204" />


                          <Line type="monotone" dataKey="val" stroke={lpu.status === 'Offline' ? '#ef4444' : '#0D4F5C'} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-1205" />

                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredData.length === 0 && <div className="text-center py-12" data-id="element-1206">
            <ActivityIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1207" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1208">No LPUs found</h3>
            <p className="text-gray-500" data-id="element-1209">
              Try adjusting your search or filters.
            </p>
          </div>}
      </div>

      {/* Detail Panel Backdrop */}
      {selectedLpuId && <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={() => setSelectedLpuId(null)} data-id="element-1210" />}

      {/* Detail Panel */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-[500px] bg-white z-50 shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out overflow-y-auto ${selectedLpuId ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-1211">

        {selectedLpu && <div className="flex flex-col h-full" data-id="element-1212">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex justify-between items-start bg-gray-50" data-id="element-1213">
              <div data-id="element-1214">
                <div className="flex items-center gap-2 mb-2" data-id="element-1215">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-none text-xs font-medium ${getTypeBadgeClass(selectedLpu.type)}`} data-id="element-1216">

                    {selectedLpu.type}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-none text-xs font-medium ${getStatusBadgeClass(selectedLpu.status)}`} data-id="element-1217">

                    {selectedLpu.status}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 leading-tight" data-id="element-1218">
                  {selectedLpu.name}
                </h2>
                <div className="text-xs text-gray-500 flex items-center gap-1 mt-2" data-id="element-1219">
                  Last updated: {selectedLpu.lastComm}
                </div>
              </div>
              <button onClick={() => setSelectedLpuId(null)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-none transition-colors" data-id="element-1220">

                <XIcon className="w-6 h-6" data-id="element-1221" />
              </button>
            </div>

            <div className="p-6 space-y-8 flex-1" data-id="element-1222">
              {/* Info Section */}
              <section data-id="element-1223">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-1224">
                  LPU Details
                </h3>
                <div className="space-y-3" data-id="element-1225">
                  <div className="flex items-start gap-3" data-id="element-1226">
                    <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1227" />
                    <div data-id="element-1228">
                      <p className="text-sm text-gray-500" data-id="element-1229">Location</p>
                      <p className="font-medium text-gray-900" data-id="element-1230">
                        {selectedLpu.location}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-50" data-id="element-1231">
                    <div data-id="element-1232">
                      <p className="text-sm text-gray-500" data-id="element-1233">Meter ID</p>
                      <p className="font-medium text-gray-900 font-mono" data-id="element-1234">
                        {selectedLpu.meterId}
                      </p>
                    </div>
                    <div data-id="element-1235">
                      <p className="text-sm text-gray-500" data-id="element-1236">LPU ID</p>
                      <p className="font-medium text-gray-900 font-mono" data-id="element-1237">
                        {selectedLpu.id}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Consumption Stats */}
              <section className="text-white p-6 rounded-none shadow-inner bg-[#002F3E]" data-id="element-1238">
                <h3 className="text-sm font-medium text-teal-100 uppercase tracking-wider mb-4" data-id="element-1239">
                  Current Billing Cycle
                </h3>
                <div className="grid grid-cols-2 gap-6" data-id="element-1240">
                  <div className="col-span-2" data-id="element-1241">
                    <p className="text-xs text-teal-200 uppercase tracking-wider mb-1" data-id="element-1242">
                      Monthly Consumption
                    </p>
                    <div className="flex items-baseline gap-2" data-id="element-1243">
                      <span className="text-4xl font-bold font-mono" data-id="element-1244">
                        {selectedLpu.consumption.toLocaleString()}
                      </span>
                      <span className="text-teal-200" data-id="element-1245">kWh</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/10" data-id="element-1246">
                    <p className="text-xs text-teal-200 uppercase tracking-wider mb-1" data-id="element-1247">
                      Peak Demand
                    </p>
                    <div className="flex items-baseline gap-1" data-id="element-1248">
                      <span className="text-xl font-bold font-mono" data-id="element-1249">
                        {selectedLpu.peakDemand.toFixed(1)}
                      </span>
                      <span className="text-sm text-teal-200" data-id="element-1250">MVA</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/10" data-id="element-1251">
                    <p className="text-xs text-teal-200 uppercase tracking-wider mb-1" data-id="element-1252">
                      Estimated Bill
                    </p>
                    <div className="flex items-baseline gap-1" data-id="element-1253">
                      <span className="text-xl font-bold font-mono" data-id="element-1254">
                        {formatZAR(selectedLpu.monthlyBill)}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Chart */}
              <section data-id="element-1255">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2" data-id="element-1256">
                  7-Day Consumption Trend
                </h3>
                <div className="h-48 w-full" data-id="element-1257">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1258">
                    <AreaChart data={selectedLpu.history.map((val, i) => ({
                  day: daysOfWeek[i],
                  value: val
                }))} margin={{
                  top: 10,
                  right: 0,
                  left: -20,
                  bottom: 0
                }} data-id="element-1259">

                      <defs data-id="element-1260">
                        <linearGradient id="colorLpuHistory" x1="0" y1="0" x2="0" y2="1" data-id="element-1261">

                          <stop offset="5%" stopColor="#0D4F5C" stopOpacity={0.3} data-id="element-1262" />


                          <stop offset="95%" stopColor="#0D4F5C" stopOpacity={0} data-id="element-1263" />

                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-1264" />


                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                    fill: '#6b7280',
                    fontSize: 12
                  }} dy={10} data-id="element-1265" />


                      <YAxis axisLine={false} tickLine={false} tick={{
                    fill: '#6b7280',
                    fontSize: 12
                  }} tickFormatter={val => `${val / 1000}k`} data-id="element-1266" />


                      <Tooltip formatter={(value: number) => [`${value.toLocaleString()} kWh`, 'Consumption']} contentStyle={{
                    borderRadius: '0',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} data-id="element-1267" />


                      <Area type="monotone" dataKey="value" stroke="#0D4F5C" strokeWidth={2} fillOpacity={1} fill="url(#colorLpuHistory)" data-id="element-1268" />

                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </section>
            </div>

            {/* Quick Actions Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50 grid grid-cols-1 gap-3" data-id="element-1269">
              <button className="w-full py-3 bg-mavis-teal text-white font-medium rounded-none hover:bg-mavis-teal/90 transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-1270">
                <FileTextIcon className="w-4 h-4" data-id="element-1271" /> View Full Report
              </button>
              <div className="grid grid-cols-2 gap-3" data-id="element-1272">
                <button className="w-full py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-none hover:bg-gray-50 transition-colors text-sm shadow-sm flex items-center justify-center gap-2" data-id="element-1273">
                  <BellIcon className="w-4 h-4" data-id="element-1274" /> Set Alert Threshold
                </button>
                <button className="w-full py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-none hover:bg-gray-50 transition-colors text-sm shadow-sm flex items-center justify-center gap-2" data-id="element-1275">
                  <DownloadIcon className="w-4 h-4" data-id="element-1276" /> Download Data
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}